import { useState } from "react";
import { Mail, MapPin } from "lucide-react";
import { Container, Button, Input, Select } from "@/components/atoms";
import { FormField } from "@/components/molecules";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/services/firebase";
import { Seo } from "@/components/seo";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // DRB-34: these were UNCONTROLLED inputs. Nothing read them, because nothing needed to —
  // submit() discarded the message and showed "Message sent!" regardless. The text the
  // customer typed never left the DOM.
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "Product question",
    message: "",
  });
  const channels = [
    {
      icon: Mail,
      label: "Email us",
      value: "orders@drburnsreleaf.com",
      href: "mailto:orders@drburnsreleaf.com",
    },
    { icon: MapPin, label: "Based in", value: "Scottsdale, Arizona" },
  ];

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      // The write is what triggers onContactSubmission, which emails orders@ AND sends the
      // customer an acknowledgement carrying every field they filled in.
      await addDoc(collection(db, "contactSubmissions"), {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject,
        message: form.message.trim(),
        createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch (err) {
      // Say the true thing. Claiming "Message sent!" when the write failed is exactly the
      // bug this replaced, and it costs a customer who thinks they have reached us.
      console.error("[contact] submission failed", err);
      setError(
        "Something went wrong sending that. Please email us directly at orders@drburnsreleaf.com and we will get right back to you.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* DRB-72 — this route set NO description, so it inherited index.html's generic one and
          described the homepage in every search result and every share. */}
      <Seo
        title="Contact"
        path="/contact"
        description="Questions about a formula, an order or wholesale? Reach Dr. Burns’ ReLeaf directly."
      />

      <Container className="grid gap-10 py-12 md:grid-cols-2">
        <div>
          <h1 className="font-condensed text-4xl font-bold text-night-blue">
            Get in touch
          </h1>
          <p className="mt-2 text-night-blue/70">
            Questions about a formula or your order? We’re here to help.
          </p>
          <div className="mt-6 flex flex-col gap-4">
            {channels.map((c) => (
              <div
                key={c.label}
                className="flex items-center gap-3 rounded-card border border-night-blue/10 p-4"
              >
                <c.icon size={22} className="text-leaf-green-ink" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-night-blue/70">
                    {c.label}
                  </p>
                  {c.href ? (
                    <a
                      href={c.href}
                      className="font-bold text-night-blue hover:text-leaf-green-ink"
                    >
                      {c.value}
                    </a>
                  ) : (
                    <p className="font-bold text-night-blue">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-card border border-night-blue/10 p-6">
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <h2 className="font-condensed text-2xl font-bold text-leaf-green-ink">
                Message sent!
              </h2>
              <p className="text-night-blue/70">
                We’ll get back to you within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <h2 className="font-condensed text-xl font-bold text-night-blue">
                Send a message
              </h2>
              <FormField label="Name" htmlFor="c-name" required>
                <Input
                  id="c-name"
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </FormField>
              <FormField label="Email" htmlFor="c-email" required>
                <Input
                  id="c-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </FormField>
              <FormField label="Subject" htmlFor="c-subject">
                <Select
                  id="c-subject"
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                >
                  <option>Product question</option>
                  <option>Order help</option>
                  <option>Subscription</option>
                  <option>Wholesale</option>
                  <option>Other</option>
                </Select>
              </FormField>
              <FormField label="Message" htmlFor="c-message" required>
                <textarea
                  id="c-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="w-full rounded-soft border border-night-blue/15 bg-white px-4 py-2 text-sm text-night-blue focus:border-leaf-green focus:outline-none focus:ring-1 focus:ring-leaf-green"
                />
              </FormField>
              {error && <p className="text-sm text-happy-red-ink">{error}</p>}
              <Button type="submit" disabled={sending}>
                {sending ? "Sending…" : "Send Message"}
              </Button>
            </form>
          )}
        </div>
      </Container>
    </>
  );
}
